/**
 * LumvinWood — cursor-tracking zoom on the main product image.
 *
 * Ports the design's zoomMove / zoomLeave behaviour: the image scales to 1.8×
 * around the pointer while hovering the main gallery frame. Horizon's own
 * click-to-open zoom dialog keeps working untouched — this only adds a
 * transform on the image element itself.
 *
 * Pointer-coarse devices and prefers-reduced-motion are skipped entirely.
 */
(() => {
  const SCALE = 1.8;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const bind = (root = document) => {
    if (reduced.matches || !finePointer.matches) return;

    // Horizon's gallery frame, and the LumvinWood PDP gallery frame
    // (sections/lumvin-pdp-main.liquid) which is a <button> around the image.
    const frames = root.querySelectorAll?.(
      'media-gallery .product-media-container--image:not([data-lw-zoom-bound]), [data-lw-zoom-frame]:not([data-lw-zoom-bound])'
    );
    if (!frames) return;

    frames.forEach((frame) => {
      // Never inside the fullscreen zoom dialog — it has its own drag-zoom.
      if (frame.closest('.dialog-zoomed-gallery')) return;

      const img = frame.querySelector('img, .product-media__image');
      if (!img) return;

      frame.dataset.lwZoomBound = '';
      frame.classList.add('lumvin-zoom-frame');

      let frameId = null;

      const move = (event) => {
        if (frameId) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(() => {
          const rect = frame.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;
          img.style.transformOrigin = `${x.toFixed(1)}% ${y.toFixed(1)}%`;
          img.style.transform = `scale(${SCALE})`;
        });
      };

      const leave = () => {
        if (frameId) cancelAnimationFrame(frameId);
        frameId = null;
        img.style.transform = 'scale(1)';
      };

      frame.addEventListener('mousemove', move);
      frame.addEventListener('mouseleave', leave);
      // A dialog opening or a variant swap must not leave the image magnified.
      frame.addEventListener('click', leave);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bind(), { once: true });
  } else {
    bind();
  }

  document.addEventListener('shopify:section:load', (e) => bind(e.target));

  // Horizon re-renders the gallery on variant change.
  new MutationObserver((records) => {
    for (const rec of records) {
      for (const node of rec.addedNodes) {
        if (node.nodeType === 1) bind(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
